import dayjs from 'dayjs';
import {
  fetchAggregatedDataJson,
  fetchDataJson
} from '../content/dashboards/Installations/fetchData';
import { FetchResult } from '../dataCache/dataCache';
import { I_S3Credentials } from './S3Types';
import { TimeSpan, UnixTime } from '../dataCache/time';
import { DataRecord } from '../dataCache/data';
import axiosConfig from '../Resources/axiosConfig';
import { AxiosError, AxiosResponse } from 'axios';
import { JSONRecordData } from '../content/dashboards/Log/graph.util';
import get from 'lodash/get';

export interface chartInfoInterface {
  magnitude: number;
  unit: string;
  min: number;
  max: number;
}

export interface overviewInterface {
  soc: chartInfoInterface;
  temperature: chartInfoInterface;
  dcPower: chartInfoInterface;
  dcPowerWithoutHeating: chartInfoInterface;
  gridPower: chartInfoInterface;
  pvProduction: chartInfoInterface;
  dcBusVoltage: chartInfoInterface;
  overview: chartInfoInterface;
  ACLoad: chartInfoInterface;
  DCLoad: chartInfoInterface;
  batteryVoltage: chartInfoInterface;
}

export interface chartAggregatedDataInterface {
  minsoc: { name: string; data: number[] };
  maxsoc: { name: string; data: number[] };
  pvProduction: { name: string; data: number[] };
  dcChargingPower: { name: string; data: number[] };
  dcDischargingPower: { name: string; data: number[] };
  gridImportPower: { name: string; data: number[] };
  gridExportPower: { name: string; data: number[] };
  heatingPower: { name: string; data: number[] };
  acLoad: { name: string; data: number[] };
}

export interface chartDataInterface {
  soc: { name: string; data: number[] };
  temperature: { name: string; data: number[] };
  dcPower: { name: string; data: number[] };
  gridPower: { name: string; data: number[] };
  pvProduction: { name: string; data: number[] };
  dcBusVoltage: { name: string; data: number[] };
  ACLoad: { name: string; data: number[] };
  DCLoad: { name: string; data: number[] };
  batteryVoltage: { name: string; data: number[] };
}

export interface BatteryDataInterface {
  Soc: { name: string; data: [] };
  Temperature: { name: string; data: [] };
  Power: { name: string; data: [] };
  Voltage: { name: string; data: [] };
  Current: { name: string; data: [] };
  Soh?: { name: string; data: [] };
}

export interface BatteryOverviewInterface {
  Soc: chartInfoInterface;
  Temperature: chartInfoInterface;
  Power: chartInfoInterface;
  Voltage: chartInfoInterface;
  Current: chartInfoInterface;
  Soh?: chartInfoInterface;
}

export const transformInputToBatteryViewDataJson = async (
  s3Credentials: I_S3Credentials,
  id: number,
  product: number,
  start_time?: UnixTime,
  end_time?: UnixTime,
  batteryClusterNumber?: number,
  activeClusters?: Array<{ invIdx: number; clIdx: number; flatIdx: number }>
): Promise<{
  chartData: BatteryDataInterface;
  chartOverview: BatteryOverviewInterface;
}> => {
  const useActive = !!activeClusters && activeClusters.length > 0;
  const prefixes = ['', 'k', 'M', 'G', 'T'];
  const MAX_NUMBER = 9999999;
  const isSodioHome = product === 2 || product === 5;
  const categories = isSodioHome
    ? ['Soc', 'Power', 'Voltage', 'Current', 'Soh']
    : ['Soc', 'Temperature', 'Power', 'Voltage', 'Current'];
  // TODO: SodistoreGrid — update data paths when installation data format is finalized
  const pathCategories =
    product === 3 || product === 4
      ? [
          '.BatteryDeligreenDataRecord.Soc',
          '.BatteryDeligreenDataRecord.TemperaturesList.EnvironmentTemperature',
          '.BatteryDeligreenDataRecord.Power',
          '.BatteryDeligreenDataRecord.TotalBatteryVoltage',
          '.BatteryDeligreenDataRecord.BusCurrent'
        ]
      : [
          '.Soc',
          '.Temperatures.Cells.Average',
          '.Dc.Power',
          '.Dc.Voltage',
          '.Dc.Current'
        ];

  // SodiStoreGrid (product 4) keeps batteries under ListOfBatteriesRecord[cluster].Devices[id].
  // Built dynamically below from the first JSON chunk; other products use the fixed list.
  const pathsToSearch: string[] =
    product === 4
      ? []
      : [
          'Battery.Devices.1',
          'Battery.Devices.2',
          'Battery.Devices.3',
          'Battery.Devices.4',
          'Battery.Devices.5',
          'Battery.Devices.6',
          'Battery.Devices.7',
          'Battery.Devices.8',
          'Battery.Devices.9',
          'Battery.Devices.10'
        ];

  const pathsToSave = [];

  const chartData: BatteryDataInterface = {
    Soc: { name: 'State Of Charge', data: [] },
    Temperature: { name: 'Temperature', data: [] },
    Power: { name: 'Power', data: [] },
    Voltage: { name: 'Voltage', data: [] },
    Current: { name: 'Current', data: [] },
    ...(isSodioHome && { Soh: { name: 'State Of Health', data: [] } })
  };

  const chartOverview: BatteryOverviewInterface = {
    Soc: { magnitude: 0, unit: '', min: 0, max: 0 },
    Temperature: { magnitude: 0, unit: '', min: 0, max: 0 },
    Power: { magnitude: 0, unit: '', min: 0, max: 0 },
    Voltage: { magnitude: 0, unit: '', min: 0, max: 0 },
    Current: { magnitude: 0, unit: '', min: 0, max: 0 },
    ...(isSodioHome && { Soh: { magnitude: 0, unit: '', min: 0, max: 0 } })
  };

  let initialiation = true;
  let timestampArray: number[] = [];
  let adjustedTimestampArray = [];
  const timestampPromises = [];

  await axiosConfig
    .get(
      `/GetCsvTimestampsForInstallation?id=${id}&start=${start_time.ticks}&end=${end_time.ticks}`
    )
    .then((res: AxiosResponse<number[]>) => {
      timestampArray = res.data;
    })
    .catch((err: AxiosError) => {
      if (err.response && err.response.status == 401) {
        //removeToken();
        //navigate(routes.login);
      }
    });
  for (var i = 0; i < timestampArray.length; i++) {
    timestampPromises.push(
      fetchJsonDataForOneTime(
        UnixTime.fromTicks(timestampArray[i], true),
        s3Credentials
      )
    );

    const adjustedTimestamp =
      product == 0 || product == 2 || product == 3 || product == 4 || product == 5
        ? new Date(timestampArray[i] * 1000)
        : new Date(timestampArray[i] * 100000);
    //Timezone offset is negative, so we convert the timestamp to the current zone by subtracting the corresponding offset
    adjustedTimestamp.setHours(
      adjustedTimestamp.getHours() - adjustedTimestamp.getTimezoneOffset() / 60
    );
    adjustedTimestampArray.push(adjustedTimestamp);
  }

  const results: Promise<FetchResult<Record<string, JSONRecordData>>>[] =
    await Promise.all(timestampPromises);

  for (let i = 0; i < results.length; i++) {
    if (results[i] == null) {
      // Handle not available or try later case
    } else {
      const timestamp = Object.keys(results[i])[
        Object.keys(results[i]).length - 1
      ];

      const result = results[i][timestamp];

      if (isSodioHome) {
        // SodistoreHome: extract battery data from InverterRecord
        const inv = (result as any)?.InverterRecord;
        if (!inv) continue;

        // Iteration order: either active-cluster list (skips empty slots,
        // preserves flat hardware indices) or a contiguous 0..N-1 fallback.
        const iter = useActive
          ? activeClusters!.map((c) => ({
              flatIdx: c.flatIdx,
              invIdx: c.invIdx,
              clIdx: c.clIdx
            }))
          : Array.from({ length: batteryClusterNumber || 1 }, (_, k) => ({
              flatIdx: k,
              invIdx: Math.floor(k / 2),
              clIdx: k % 2
            }));

        const inverterCount = iter.reduce(
          (max, c) => Math.max(max, c.invIdx + 1),
          0
        );
        const showInverterLabel = !!inv?.Devices && inverterCount > 1;

        let old_length = pathsToSave.length;

        iter.forEach((c) => {
          const nodeName = 'Node' + c.flatIdx;
          if (!pathsToSave.includes(nodeName)) {
            pathsToSave.push(nodeName);
          }
        });

        if (initialiation) {
          initialiation = false;
          categories.forEach((category) => {
            chartData[category].data = [];
            chartOverview[category] = {
              magnitude: 0,
              unit: '',
              min: MAX_NUMBER,
              max: -MAX_NUMBER
            };
          });
        }

        if (pathsToSave.length > old_length) {
          categories.forEach((category) => {
            iter.forEach((c) => {
              const path = 'Node' + c.flatIdx;
              if (pathsToSave.indexOf(path) >= old_length) {
                const name = showInverterLabel
                  ? `Battery Cluster ${c.clIdx + 1} in Inverter ${c.invIdx + 1}`
                  : `Battery Cluster ${c.clIdx + 1}`;
                chartData[category].data[path] = { name, data: [] };
              }
            });
          });
        }

        const hasDevices = !!inv?.Devices;

        // Sinexcel field suffixes differ from Growatt for Voltage/Current
        const categoryFieldMapGrowatt = {
          Soc: 'Soc',
          Power: 'Power',
          Voltage: 'Voltage',
          Current: 'Current',
          Soh: 'Soh'
        };
        const categoryFieldMapSinexcel = {
          Soc: 'Soc',
          Power: 'Power',
          Voltage: 'PackTotalVoltage',
          Current: 'PackTotalCurrent',
          Soh: 'Soh'
        };

        iter.forEach((c) => {
          const path = 'Node' + c.flatIdx;
          categories.forEach((category) => {
            let value: number | undefined;

            if (hasDevices) {
              // Sinexcel: Devices keyed by "1","2",... (1-based dict keys)
              const device = inv.Devices[String(c.invIdx + 1)];
              const bi = c.clIdx + 1;
              const fieldName = `Battery${bi}${categoryFieldMapSinexcel[category]}`;
              value = device?.[fieldName];
              if ((value === undefined || value === null) && category === 'Soc') {
                value = device?.[`Battery${bi}SocSecondvalue`];
              }
            } else {
              // Growatt: flat Battery1Soc, Battery2Voltage, ... on InverterRecord
              const batteryIndex = c.clIdx + 1;
              const fieldName = `Battery${batteryIndex}${categoryFieldMapGrowatt[category]}`;
              value = inv[fieldName];
            }

            if (value !== undefined && value !== null) {
              if (value < chartOverview[category].min) {
                chartOverview[category].min = value;
              }
              if (value > chartOverview[category].max) {
                chartOverview[category].max = value;
              }
              chartData[category].data[path].data.push([
                adjustedTimestampArray[i],
                value
              ]);
            }
          });
        });
      } else {
        // SaliMax, Salidomo, SodistoreMax, SodistoreGrid: existing logic
        // SodistoreGrid (product 4) batteries live under ListOfBatteriesRecord[cluster].Devices[id];
        // enumerate them dynamically and build pathsToSearch in parallel with pathsToSave.
        let old_length = pathsToSave.length;

        if (product === 4) {
          const lobr = (result as any)?.ListOfBatteriesRecord ?? {};
          const clusters = Object.keys(lobr).sort(
            (a, b) => Number(a) - Number(b)
          );
          clusters.forEach((clusterId) => {
            const devices = Object.keys(lobr[clusterId]?.Devices ?? {}).sort(
              (a, b) => Number(a) - Number(b)
            );
            devices.forEach((deviceId) => {
              const nodeName = `Node${clusterId}-${deviceId}`;
              if (!pathsToSave.includes(nodeName)) {
                pathsToSave.push(nodeName);
                pathsToSearch.push(
                  `ListOfBatteriesRecord.${clusterId}.Devices.${deviceId}`
                );
              }
            });
          });
        } else {
          const battery_nodes =
            result.Config.Devices.BatteryNodes.toString().split(',');

          //Initialize the chartData structure based on the node names extracted from the first result
          if (battery_nodes.length > old_length) {
            battery_nodes.forEach((node) => {
              const node_number =
                product == 3 ? Number(node) + 1 : Number(node) - 1;
              if (!pathsToSave.includes('Node' + node_number)) {
                pathsToSave.push('Node' + node_number);
              }
            });
          }
        }

        if (initialiation) {
          initialiation = false;
          categories.forEach((category) => {
            chartData[category].data = [];
            chartOverview[category] = {
              magnitude: 0,
              unit: '',
              min: MAX_NUMBER,
              max: -MAX_NUMBER
            };
          });
        }

        if (pathsToSave.length > old_length) {
          categories.forEach((category) => {
            pathsToSave.forEach((path) => {
              if (pathsToSave.indexOf(path) >= old_length) {
                chartData[category].data[path] = { name: path, data: [] };
              }
            });
          });
        }

        for (
          let category_index = 0;
          category_index < pathCategories.length;
          category_index++
        ) {
          let category = categories[category_index];

          for (let j = 0; j < pathsToSave.length; j++) {
            let path = pathsToSearch[j] + pathCategories[category_index];

            if (get(result, path) !== undefined) {
              const value = path
                .split('.')
                .reduce((o, key) => (o ? o[key] : undefined), result);

              if (value < chartOverview[category].min) {
                chartOverview[category].min = value;
              }

              if (value > chartOverview[category].max) {
                chartOverview[category].max = value;
              }
              chartData[category].data[pathsToSave[j]].data.push([
                adjustedTimestampArray[i],
                value
              ]);
            }
          }
        }
      }
    }
  }

  categories.forEach((category) => {
    let value = Math.max(
      Math.abs(chartOverview[category].max),
      Math.abs(chartOverview[category].min)
    );
    let magnitude = 0;

    if (value < 0) {
      value = -value;
    }
    while (value >= 1000) {
      value /= 1000;
      magnitude++;
    }
    chartOverview[category].magnitude = magnitude;
  });

  chartOverview.Soc.unit = '(%)';
  chartOverview.Soc.min = 0;
  chartOverview.Soc.max = 100;
  if (!isSodioHome) {
    chartOverview.Temperature.unit = '(°C)';
  }
  chartOverview.Power.unit =
    '(' + prefixes[chartOverview['Power'].magnitude] + 'W' + ')';
  chartOverview.Voltage.unit =
    '(' + prefixes[chartOverview['Voltage'].magnitude] + 'V' + ')';
  chartOverview.Current.unit =
    '(' + prefixes[chartOverview['Current'].magnitude] + 'A' + ')';
  if (isSodioHome) {
    chartOverview.Soh.unit = '(%)';
    chartOverview.Soh.min = 0;
    chartOverview.Soh.max = 100;
  }

  return {
    chartData: chartData,
    chartOverview: chartOverview
  };
};

export const transformInputToDailyDataJson = async (
  product: number,
  s3Credentials: I_S3Credentials,
  id: number,
  start_time?: UnixTime,
  end_time?: UnixTime
): Promise<{
  chartData: chartDataInterface;
  chartOverview: overviewInterface;
}> => {
  const prefixes = ['', 'k', 'M', 'G', 'T'];

  const MAX_NUMBER = 9999999;
  // For SodioHome (product=2), paths are placeholders — actual extraction uses
  // custom fallback logic to handle differences between Growatt and Sinexcel.
  // Growatt has: Battery1AmbientTemperature, GridPower, PvPower
  // Sinexcel has: Battery1Temperature, TotalGridPower (meter may be offline), PvPower1-4
  const pathsToSearch = (product == 2 || product == 5)
    ? [
        'SODIOHOME_SOC',
        'SODIOHOME_TEMPERATURE',
        'SODIOHOME_BATTERY_POWER',
        'SODIOHOME_GRID_POWER',
        'SODIOHOME_PV_POWER',
        null, // dcBusVoltage not available for SodioHome
        'SODIOHOME_CONSUMPTION',
        null, // DCLoad not available for SodioHome
        'SODIOHOME_BATTERY_VOLTAGE'
      ]
    : product == 4
    ? [
        // SodistoreGrid: placeholders — actual extraction runs in the
        // product===4 switch below; nulls only mark "no data path", so
        // the forEach skips the irrelevant categories. Battery voltage
        // (index 8) MUST be a non-null placeholder, otherwise the entry
        // is skipped before the switch can populate it.
        'ListOfBatteriesRecord',         // 0 soc
        'ListOfBatteriesRecord',         // 1 temperature
        'ListOfBatteriesRecord',         // 2 dcPower (battery power)
        null,                            // 3 gridPower — no grid meter
        null,                            // 4 pvProduction — no PV
        'DcDc.Dc.Link.Voltage',          // 5 dcBusVoltage
        null,                            // 6 ACLoad — no AC load meter
        null,                            // 7 DCLoad — no DC load meter
        'ListOfBatteriesRecord'          // 8 batteryVoltage (cluster avg)
      ]
    : [
        'Battery.Soc',
        product == 0 ? 'Battery.Temperature' : 'Battery.TemperatureCell1',
        product == 0 ? 'Battery.Dc.Power' : 'Battery.Power',
        'GridMeter.Ac.Power.Active',
        'PvOnDc',
        'DcDc.Dc.Link.Voltage',
        'LoadOnAcGrid.Power.Active',
        'LoadOnDc.Power',
        null // batteryVoltage not available for Salimax
      ];
  const categories = [
    'soc',
    'temperature',
    'dcPower',
    'gridPower',
    'pvProduction',
    'dcBusVoltage',
    'ACLoad',
    'DCLoad',
    'batteryVoltage'
  ];

  const chartData: chartDataInterface = {
    soc: { name: 'Battery SOC', data: [] },
    temperature: { name: 'Battery Temperature', data: [] },
    dcPower: { name: 'Battery Power', data: [] },
    gridPower: { name: 'Grid Power', data: [] },
    pvProduction: { name: 'PV Power', data: [] },
    dcBusVoltage: { name: 'DC Bus Voltage', data: [] },
    ACLoad: { name: 'AC Load', data: [] },
    DCLoad: { name: 'DC Load', data: [] },
    batteryVoltage: { name: 'Battery Voltage', data: [] }
  };

  const chartOverview: overviewInterface = {
    soc: { magnitude: 0, unit: '', min: 0, max: 0 },
    temperature: { magnitude: 0, unit: '', min: 0, max: 0 },
    dcPower: { magnitude: 0, unit: '', min: 0, max: 0 },
    dcPowerWithoutHeating: { magnitude: 0, unit: '', min: 0, max: 0 },
    gridPower: { magnitude: 0, unit: '', min: 0, max: 0 },
    pvProduction: { magnitude: 0, unit: '', min: 0, max: 0 },
    dcBusVoltage: { magnitude: 0, unit: '', min: 0, max: 0 },
    overview: { magnitude: 0, unit: '', min: 0, max: 0 },
    ACLoad: { magnitude: 0, unit: '', min: 0, max: 0 },
    DCLoad: { magnitude: 0, unit: '', min: 0, max: 0 },
    batteryVoltage: { magnitude: 0, unit: '', min: 0, max: 0 }
  };

  categories.forEach((category) => {
    chartData[category].data = [];
    chartOverview[category] = {
      magnitude: 0,
      unit: '',
      min: MAX_NUMBER,
      max: -MAX_NUMBER
    };
  });

  let timestampArray: number[] = [];
  let adjustedTimestampArray = [];
  const timestampPromises = [];

  await axiosConfig
    .get(
      `/GetCsvTimestampsForInstallation?id=${id}&start=${start_time.ticks}&end=${end_time.ticks}`
    )
    .then((res: AxiosResponse<number[]>) => {
      timestampArray = res.data;
    })
    .catch((err: AxiosError) => {
      if (err.response && err.response.status == 401) {
        //removeToken();
        //navigate(routes.login);
      }
    });

  for (var i = 0; i < timestampArray.length; i++) {
    timestampPromises.push(
      fetchJsonDataForOneTime(
        UnixTime.fromTicks(timestampArray[i]),
        s3Credentials
      )
    );

    const adjustedTimestamp = new Date(timestampArray[i] * 1000);
    //Timezone offset is negative, so we convert the timestamp to the current zone by subtracting the corresponding offset
    adjustedTimestamp.setHours(
      adjustedTimestamp.getHours() - adjustedTimestamp.getTimezoneOffset() / 60
    );
    adjustedTimestampArray.push(adjustedTimestamp);
  }

  const results: Promise<FetchResult<Record<string, JSONRecordData>>>[] =
    await Promise.all(timestampPromises);

  for (let i = 0; i < results.length; i++) {
    if (results[i] == null) {
      // Handle not available or try later case
    } else {
      const timestamp = Object.keys(results[i])[
        Object.keys(results[i]).length - 1
      ];
      const result = results[i][timestamp];
      const { Config: { S3: { Key: _k, Secret: _s, ...s3Rest } = {} as any, ...configRest } = {} as any, ...dataRest } = result || {};
      console.log('Overview data:', { ...dataRest, Config: { ...configRest, S3: { ...s3Rest, Key: '***', Secret: '***' } } });
      let category_index = 0;
      // eslint-disable-next-line @typescript-eslint/no-loop-func
      pathsToSearch.forEach((path) => {
        if (path === null) {
          // Skip unavailable fields (e.g. dcBusVoltage, DCLoad for SodioHome)
          category_index++;
          return;
        }

        let value: number | undefined = undefined;

        if (product === 2 || product === 5) {
          // SodioHome/SodistorePro: use top-level aggregated values (Sinexcel multi-inverter)
          const inv = result?.InverterRecord;
          if (inv) {
            switch (category_index) {
              case 0: // soc
                value = inv.AvgBatterySoc ?? inv.Battery1Soc;
                break;
              case 1: // temperature
                value = inv.AvgBatteryTemp ?? inv.Battery1AmbientTemperature ?? inv.Battery1Temperature;
                break;
              case 2: // battery power
                value = inv.TotalBatteryPower ?? inv.Battery1Power;
                break;
              case 3: // grid power
                value = inv.TotalGridPower ?? inv.GridPower;
                break;
              case 4: // pv production
                value = inv.TotalPhotovoltaicPower ?? inv.PvPower ?? inv.PvTotalPower;
                break;
              case 6: // consumption
                value = inv.TotalLoadPower ?? inv.ConsumptionPower;
                break;
              case 8: // battery voltage
                value = inv.AvgBatteryVoltage ?? inv.Battery1Voltage;
                break;
            }
          }
        } else if (product === 4) {
          // SodiStoreGrid: only battery + DC-side metrics exist.
          // No grid meter, no PV, no AC/DC load on this product — those series stay empty.
          const lobr: Record<string, any> =
            (result as any)?.ListOfBatteriesRecord ?? {};
          const clusterValues = Object.values(lobr) as any[];
          const avgOf = (pick: (c: any) => number | undefined): number | undefined => {
            const xs = clusterValues
              .map(pick)
              .filter((v): v is number => typeof v === 'number');
            return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : undefined;
          };
          const sumOf = (pick: (c: any) => number | undefined): number | undefined => {
            const xs = clusterValues
              .map(pick)
              .filter((v): v is number => typeof v === 'number');
            return xs.length ? xs.reduce((a, b) => a + b, 0) : undefined;
          };
          switch (category_index) {
            case 0: // soc
              value = avgOf((c) => c?.Soc);
              break;
            case 1: // temperature
              value = avgOf((c) => c?.TemperatureCell1);
              break;
            case 2: // battery power (sum of cluster powers, else DcDc battery-side)
              value =
                sumOf((c) => c?.Power) ??
                (result as any)?.DcDc?.Dc?.Battery?.Power;
              break;
            case 5: // dc bus voltage
              value = (result as any)?.DcDc?.Dc?.Link?.Voltage;
              break;
            case 8: // battery voltage (cluster average, else DcDc battery side)
              value =
                avgOf((c) => c?.Voltage) ??
                (result as any)?.DcDc?.Dc?.Battery?.Voltage;
              break;
            // case 3 (grid), 4 (PV), 6 (AC load), 7 (DC load) — not available on SodiStoreGrid
          }
        } else if (category_index === 4) {
          // Salimax/Salidomo: Custom logic for 'PvOnDc.Dc.Power'
          if (get(result, path) !== undefined) {
            value = Object.values(
              result.PvOnDc as Record<string, { Dc?: { Power?: number } }>
            ).reduce((sum, device) => sum + (device.Dc?.Power || 0), 0);
          }
        } else if (get(result, path) !== undefined) {
          // Default path-based extraction
          value = path
            .split('.')
            .reduce((o, key) => (o ? o[key] : undefined), result);
        }

        // Only push value if defined
        if (value !== undefined) {
          if (value < chartOverview[categories[category_index]].min) {
            chartOverview[categories[category_index]].min = value;
          }

          if (value > chartOverview[categories[category_index]].max) {
            chartOverview[categories[category_index]].max = value;
          }

          chartData[categories[category_index]].data.push([
            adjustedTimestampArray[i],
            value
          ]);
        }

        category_index++;
      });
    }
  }

  categories.forEach((category) => {
    let value = Math.max(
      Math.abs(chartOverview[category].max),
      Math.abs(chartOverview[category].min)
    );
    let magnitude = 0;

    if (value < 0) {
      value = -value;
    }
    while (value >= 1000) {
      value /= 1000;
      magnitude++;
    }
    chartOverview[category].magnitude = magnitude;
  });

  // SodistoreGrid: 18 parallel battery devices easily swing into the kW range.
  // Pin Battery Power to at least kW so the axis stays stable across day/night cycles.
  if (product === 4) {
    chartOverview.dcPower.magnitude = Math.max(
      chartOverview.dcPower.magnitude,
      1
    );
  }

  chartOverview.soc.unit = '(%)';
  chartOverview.soc.min = 0;
  chartOverview.soc.max = 100;
  chartOverview.temperature.unit = '(°C)';
  chartOverview.dcPower.unit =
    '(' + prefixes[chartOverview['dcPower'].magnitude] + 'W' + ')';
  chartOverview.gridPower.unit =
    '(' + prefixes[chartOverview['gridPower'].magnitude] + 'W' + ')';
  chartOverview.pvProduction.unit =
    '(' + prefixes[chartOverview['pvProduction'].magnitude] + 'W' + ')';
  chartOverview.dcBusVoltage.unit =
    '(' + prefixes[chartOverview['dcBusVoltage'].magnitude] + 'V' + ')';
  chartOverview.ACLoad.unit =
    '(' + prefixes[chartOverview['ACLoad'].magnitude] + 'W' + ')';
  chartOverview.DCLoad.unit =
    '(' + prefixes[chartOverview['DCLoad'].magnitude] + 'W' + ')';
  chartOverview.batteryVoltage.unit = '(V)';

  chartOverview.overview = {
    magnitude: Math.max(
      chartOverview['gridPower'].magnitude,
      chartOverview['pvProduction'].magnitude,
      chartOverview['ACLoad'].magnitude
    ),
    unit: '(kW)',
    min: Math.min(
      chartOverview['gridPower'].min,
      chartOverview['pvProduction'].min,
      chartOverview['ACLoad'].min
    ),
    max: Math.max(
      chartOverview['gridPower'].max,
      chartOverview['pvProduction'].max,
      chartOverview['ACLoad'].max
    )
  };

  return {
    chartData: chartData,
    chartOverview: chartOverview
  };
};

const fetchJsonDataForOneTime = async (
  startUnixTime: UnixTime,
  s3Credentials: I_S3Credentials
): Promise<FetchResult<Record<string, DataRecord>>> => {
  var timeperiodToSearch = 2;
  let res;
  let timestampToFetch;

  for (var i = 0; i < timeperiodToSearch; i++) {
    timestampToFetch = startUnixTime.later(TimeSpan.fromSeconds(i));

    try {
      res = await fetchDataJson(timestampToFetch, s3Credentials);

      if (res !== FetchResult.notAvailable && res !== FetchResult.tryLater) {
        console.log('Successfully fetched ' + timestampToFetch);
        return res;
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  }
  console.warn('Failed to fetch timestamp ' + startUnixTime.ticks);
  return null;
};

export const transformInputToAggregatedDataJson = async (
  s3Credentials: I_S3Credentials,
  start_date: dayjs.Dayjs,
  end_date: dayjs.Dayjs,
  product?: number
): Promise<{
  chartAggregatedData: chartAggregatedDataInterface;
  chartOverview: overviewInterface;
  dateList: string[];
}> => {
  const data = {};
  const overviewData = {};
  const MAX_NUMBER = 9999999;
  const dateList = [];

  let currentDay = start_date;

  const pathsToSearch = [
    'MinSoc',
    'MaxSoc',
    'PvPower',
    'DischargingBatteryPower',
    'ChargingBatteryPower',
    'GridImportPower',
    'GridExportPower',
    'HeatingPower',
    'LoadPowerConsumption'
  ];

  const categories = [
    'minsoc',
    'maxsoc',
    'pvProduction',
    'dcChargingPower',
    'heatingPower',
    'dcDischargingPower',
    'gridImportPower',
    'gridExportPower'
  ];

  const chartAggregatedData: chartAggregatedDataInterface = {
    minsoc: { name: 'min SOC', data: [] },
    maxsoc: { name: 'max SOC', data: [] },
    pvProduction: { name: 'Pv Energy', data: [] },
    dcChargingPower: { name: 'Charging Battery Energy', data: [] },
    heatingPower: { name: 'Heating Energy', data: [] },
    dcDischargingPower: { name: 'Discharging Battery Energy', data: [] },
    gridImportPower: { name: 'Grid Import Energy', data: [] },
    gridExportPower: { name: 'Grid Export Energy', data: [] },
    acLoad: { name: 'AC Load', data: [] }
  };

  const chartOverview: overviewInterface = {
    soc: { magnitude: 0, unit: '', min: 0, max: 0 },
    temperature: { magnitude: 0, unit: '', min: 0, max: 0 },
    dcPower: { magnitude: 0, unit: '', min: 0, max: 0 },
    dcPowerWithoutHeating: { magnitude: 0, unit: '', min: 0, max: 0 },
    gridPower: { magnitude: 0, unit: '', min: 0, max: 0 },
    pvProduction: { magnitude: 0, unit: '', min: 0, max: 0 },
    dcBusVoltage: { magnitude: 0, unit: '', min: 0, max: 0 },
    overview: { magnitude: 0, unit: '', min: 0, max: 0 },
    ACLoad: { magnitude: 0, unit: '', min: 0, max: 0 },
    DCLoad: { magnitude: 0, unit: '', min: 0, max: 0 },
    batteryVoltage: { magnitude: 0, unit: '', min: 0, max: 0 }
  };

  pathsToSearch.forEach((path) => {
    data[path] = [];
    overviewData[path] = {
      magnitude: 0,
      unit: '',
      min: MAX_NUMBER,
      max: -MAX_NUMBER
    };
  });

  const timestampPromises = [];

  while (currentDay.isBefore(end_date)) {
    const dateFormat = (product === 2 || product === 5)
      ? currentDay.format('DDMMYYYY')
      : currentDay.format('YYYY-MM-DD');
    timestampPromises.push(
      fetchAggregatedDataJson(dateFormat, s3Credentials, product)
    );
    currentDay = currentDay.add(1, 'day');
  }

  const results = await Promise.all(timestampPromises);
  console.log("Fetched aggregated daily results: [count=" + results.length + "]");
  currentDay = start_date;

  for (let i = 0; i < results.length; i++) {
    const result = results[i];
    if (
      result === FetchResult.notAvailable ||
      result === FetchResult.tryLater
    ) {
      // Handle not available or try later case
    } else {
      // console.log(result);
      dateList.push(currentDay.format('DD-MM'));
      pathsToSearch.forEach((path) => {
        const value = path
          .split('.')
          .reduce((o, key) => (o ? o[key] : undefined), result);

        if (value !== undefined) {
          if (path === '.GridExportPower') {
            result.GridExportPower = -value;
          }
          if (value < overviewData[path].min) {
            overviewData[path].min = value;
          }
          if (value > overviewData[path].max) {
            overviewData[path].max = value;
          }
          data[path].push(value as number);
        }
      });
    }
    currentDay = currentDay.add(1, 'day');
  }

  pathsToSearch.forEach((path) => {
    let value = Math.max(
      Math.abs(overviewData[path].max),
      Math.abs(overviewData[path].min)
    );
    let magnitude = 0;

    if (value < 0) {
      value = -value;
    }
    while (value >= 1000) {
      value /= 1000;
      magnitude++;
    }
    overviewData[path].magnitude = magnitude;
  });

  let path = 'MinSoc';
  chartAggregatedData.minsoc.data = data[path];

  path = 'MaxSoc';
  chartAggregatedData.maxsoc.data = data[path];

  chartOverview.soc = {
    unit: '(%)',
    magnitude: overviewData[path].magnitude,
    min: 0,
    max: 100
  };

  path = 'PvPower';
  chartAggregatedData.pvProduction.data = data[path];

  chartOverview.pvProduction = {
    magnitude: overviewData[path].magnitude,
    unit: '(kWh)',
    min: overviewData[path].min,
    max: overviewData[path].max
  };

  path = 'ChargingBatteryPower';
  chartAggregatedData.dcChargingPower.data = data[path];

  path = 'DischargingBatteryPower';
  chartAggregatedData.dcDischargingPower.data = data[path];

  path = 'HeatingPower';
  chartAggregatedData.heatingPower.data = data[path];

  chartOverview.dcPowerWithoutHeating = {
    magnitude: Math.max(
      overviewData['ChargingBatteryPower'].magnitude,
      overviewData['DischargingBatteryPower'].magnitude
    ),
    unit: '(kWh)',
    min: overviewData['DischargingBatteryPower'].min,
    max: overviewData['ChargingBatteryPower'].max
  };

  chartOverview.dcPower = {
    magnitude: Math.max(
      overviewData['ChargingBatteryPower'].magnitude,
      overviewData['HeatingPower'].magnitude,
      overviewData['DischargingBatteryPower'].magnitude
    ),
    unit: '(kWh)',
    min: overviewData['DischargingBatteryPower'].min,
    max:
      overviewData['ChargingBatteryPower'].max +
      overviewData['HeatingPower'].max
  };

  path = 'GridImportPower';
  chartAggregatedData.gridImportPower.data = data[path];

  path = 'GridExportPower';
  chartAggregatedData.gridExportPower.data = data[path];

  chartOverview.gridPower = {
    magnitude: Math.max(
      overviewData['GridImportPower'].magnitude,
      overviewData['GridExportPower'].magnitude
    ),
    unit: '(kWh)',
    min: overviewData['GridExportPower'].min,
    max: overviewData['GridImportPower'].max
  };

  path = 'LoadPowerConsumption';
  chartAggregatedData.acLoad.data = data[path];

  chartOverview.ACLoad = {
    magnitude: overviewData['LoadPowerConsumption'].magnitude,
    unit: '(kWh)',
    min: overviewData['LoadPowerConsumption'].min,
    max: overviewData['LoadPowerConsumption'].max
  };

  chartOverview.overview = {
    magnitude: 0,
    unit: '(kWh)',
    min: Math.min(
      overviewData['GridImportPower'].min,
      overviewData['GridExportPower'].min,
      overviewData['PvPower'].min
    ),
    max: Math.max(
      overviewData['GridImportPower'].max,
      overviewData['GridExportPower'].max,
      overviewData['PvPower'].max
    )
  };

  return {
    chartAggregatedData: chartAggregatedData,
    chartOverview: chartOverview,
    dateList: dateList
  };
};

//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------
//-------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------------

//UNCOMMENT THE FOLLOWING FOR CSV
// We use this function in order to retrieve data for main stats.
//The data is of the following form:
//'Soc' : {name:'Soc',data:[
//                          'Node1': {name:'Node1', data: [[timestamp,value],[timestamp,value]]},
//                          'Node2': {name:'Node2', data: [[timestamp,value],[timestamp,value]]},
//        ]},
//'Temperature' : {name:'Temperature',data:[
//                          'Node1': {name:'Node1', data: [[timestamp,value],[timestamp,value]]},
//                          'Node2': {name:'Node2', data: [[timestamp,value],[timestamp,value]]},
//        ]}
//
// export const transformInputToBatteryViewData = async (
//   s3Credentials: I_S3Credentials,
//   id: number,
//   product: number,
//   start_time?: UnixTime,
//   end_time?: UnixTime
// ): Promise<{
//   chartData: BatteryDataInterface;
//   chartOverview: BatteryOverviewInterface;
// }> => {
//   const prefixes = ['', 'k', 'M', 'G', 'T'];
//   const MAX_NUMBER = 9999999;
//   const categories = ['Soc', 'Temperature', 'Power', 'Voltage', 'Current'];
//   const pathCategories = [
//     'Soc',
//     'Temperatures/Cells/Average',
//     'Dc/Power',
//     'Dc/Voltage',
//     'Dc/Current'
//   ];
//
//   const pathsToSearch = [
//     '/Battery/Devices/1/',
//     '/Battery/Devices/2/',
//     '/Battery/Devices/3/',
//     '/Battery/Devices/4/',
//     '/Battery/Devices/5/',
//     '/Battery/Devices/6/',
//     '/Battery/Devices/7/',
//     '/Battery/Devices/8/',
//     '/Battery/Devices/9/',
//     '/Battery/Devices/10/'
//   ];
//
//   const pathsToSave = [];
//
//   const chartData: BatteryDataInterface = {
//     Soc: { name: 'State Of Charge', data: [] },
//     Temperature: { name: 'Temperature', data: [] },
//     Power: { name: 'Power', data: [] },
//     Voltage: { name: 'Voltage', data: [] },
//     Current: { name: 'Voltage', data: [] }
//   };
//
//   const chartOverview: BatteryOverviewInterface = {
//     Soc: { magnitude: 0, unit: '', min: 0, max: 0 },
//     Temperature: { magnitude: 0, unit: '', min: 0, max: 0 },
//     Power: { magnitude: 0, unit: '', min: 0, max: 0 },
//     Voltage: { magnitude: 0, unit: '', min: 0, max: 0 },
//     Current: { magnitude: 0, unit: '', min: 0, max: 0 }
//   };
//
//   let initialiation = true;
//
//   let timestampArray: number[] = [];
//   let adjustedTimestampArray = [];
//   const timestampPromises = [];
//
//   await axiosConfig
//     .get(
//       `/GetCsvTimestampsForInstallation?id=${id}&start=${start_time.ticks}&end=${end_time.ticks}`
//     )
//     .then((res: AxiosResponse<number[]>) => {
//       timestampArray = res.data;
//     })
//     .catch((err: AxiosError) => {
//       if (err.response && err.response.status == 401) {
//         //removeToken();
//         //navigate(routes.login);
//       }
//     });
//
//   for (var i = 0; i < timestampArray.length; i++) {
//     timestampPromises.push(
//       fetchDataForOneTime(
//         UnixTime.fromTicks(timestampArray[i], true),
//         s3Credentials
//       )
//     );
//
//     const adjustedTimestamp =
//       product == 0
//         ? new Date(timestampArray[i] * 1000)
//         : new Date(timestampArray[i] * 100000);
//     //Timezone offset is negative, so we convert the timestamp to the current zone by subtracting the corresponding offset
//     adjustedTimestamp.setHours(
//       adjustedTimestamp.getHours() - adjustedTimestamp.getTimezoneOffset() / 60
//     );
//     adjustedTimestampArray.push(adjustedTimestamp);
//   }
//
//   const results: Promise<FetchResult<Record<string, DataRecord>>>[] =
//     await Promise.all(timestampPromises);
//
//   for (let i = 0; i < results.length; i++) {
//     if (results[i] == null) {
//       // Handle not available or try later case
//     } else {
//       const timestamp = Object.keys(results[i])[
//         Object.keys(results[i]).length - 1
//       ];
//       const result = results[i][timestamp];
//       const battery_nodes = result['/Config/Devices/BatteryNodes'].value
//         .toString()
//         .split(',');
//
//       //Initialize the chartData structure based on the node names extracted from the first result
//       let old_length = pathsToSave.length;
//
//       if (battery_nodes.length > old_length) {
//         battery_nodes.forEach((node) => {
//           if (!pathsToSave.includes('Node' + node)) {
//             pathsToSave.push('Node' + node);
//           }
//         });
//       }
//
//       if (initialiation) {
//         initialiation = false;
//         categories.forEach((category) => {
//           chartData[category].data = [];
//           chartOverview[category] = {
//             magnitude: 0,
//             unit: '',
//             min: MAX_NUMBER,
//             max: -MAX_NUMBER
//           };
//         });
//       }
//
//       if (battery_nodes.length > old_length) {
//         categories.forEach((category) => {
//           pathsToSave.forEach((path) => {
//             if (pathsToSave.indexOf(path) >= old_length) {
//               chartData[category].data[path] = { name: path, data: [] };
//             }
//           });
//         });
//       }
//
//       for (
//         let category_index = 0;
//         category_index < pathCategories.length;
//         category_index++
//       ) {
//         let category = categories[category_index];
//
//         for (let j = 0; j < pathsToSave.length; j++) {
//           let path = pathsToSearch[j] + pathCategories[category_index];
//
//           if (result[path]) {
//             const value = result[path];
//
//             if (value.value < chartOverview[category].min) {
//               chartOverview[category].min = value.value;
//             }
//
//             if (value.value > chartOverview[category].max) {
//               chartOverview[category].max = value.value;
//             }
//             chartData[category].data[pathsToSave[j]].data.push([
//               adjustedTimestampArray[i],
//               value.value
//             ]);
//           } else {
//             // chartData[category].data[pathsToSave[j]].data.push([
//             //   adjustedTimestampArray[i],
//             //   null
//             // ]);
//           }
//         }
//       }
//     }
//   }
//   categories.forEach((category) => {
//     let value = Math.max(
//       Math.abs(chartOverview[category].max),
//       Math.abs(chartOverview[category].min)
//     );
//     let magnitude = 0;
//
//     if (value < 0) {
//       value = -value;
//     }
//     while (value >= 1000) {
//       value /= 1000;
//       magnitude++;
//     }
//     chartOverview[category].magnitude = magnitude;
//   });
//
//   chartOverview.Soc.unit = '(%)';
//   chartOverview.Soc.min = 0;
//   chartOverview.Soc.max = 100;
//   chartOverview.Temperature.unit = '(°C)';
//   chartOverview.Power.unit =
//     '(' + prefixes[chartOverview['Power'].magnitude] + 'W' + ')';
//   chartOverview.Voltage.unit =
//     '(' + prefixes[chartOverview['Voltage'].magnitude] + 'V' + ')';
//
//   chartOverview.Current.unit =
//     '(' + prefixes[chartOverview['Current'].magnitude] + 'A' + ')';
//
//   return {
//     chartData: chartData,
//     chartOverview: chartOverview
//   };
// };

// We use this function in order to retrieve data for main stats.
// The data is of the following form:
// 'Soc' : {name:'Soc',data:[
//                          'Node1': {name:'Node1', data: [[timestamp,value],[timestamp,value]]},
//                          'Node2': {name:'Node2', data: [[timestamp,value],[timestamp,value]]},
//        ]},
// 'Temperature' : {name:'Temperature',data:[
//                          'Node1': {name:'Node1', data: [[timestamp,value],[timestamp,value]]},
//                          'Node2': {name:'Node2', data: [[timestamp,value],[timestamp,value]]},
//        ]}
//
// const fetchDataForOneTime = async (
//   startUnixTime: UnixTime,
//   s3Credentials: I_S3Credentials
// ): Promise<FetchResult<Record<string, DataRecord>>> => {
//   var timeperiodToSearch = 2;
//   let res;
//   let timestampToFetch;
//
//   for (var i = 0; i < timeperiodToSearch; i++) {
//     timestampToFetch = startUnixTime.later(TimeSpan.fromSeconds(i));
//
//     try {
//       res = await fetchData(timestampToFetch, s3Credentials);
//
//       if (res !== FetchResult.notAvailable && res !== FetchResult.tryLater) {
//         //console.log('Successfully fetched ' + timestampToFetch);
//         return res;
//       }
//     } catch (err) {
//       console.error('Error fetching data:', err);
//     }
//   }
//   return null;
// };
//
// export const transformInputToDailyData = async (
//   s3Credentials: I_S3Credentials,
//   id: number,
//   start_time?: UnixTime,
//   end_time?: UnixTime
// ): Promise<{
//   chartData: chartDataInterface;
//   chartOverview: overviewInterface;
// }> => {
//   //const navigate = useNavigate();
//   //const tokencontext = useContext(TokenContext);
//   //const { removeToken } = tokencontext;
//   const prefixes = ['', 'k', 'M', 'G', 'T'];
//   const MAX_NUMBER = 9999999;
//   const pathsToSearch = [
//     '/Battery/Soc',
//     '/Battery/Temperature',
//     '/Battery/Dc/Power',
//     '/GridMeter/Ac/Power/Active',
//     '/PvOnDc/Dc/Power',
//     '/DcDc/Dc/Link/Voltage',
//     '/LoadOnAcGrid/Power/Active',
//     '/LoadOnDc/Power'
//   ];
//   const categories = [
//     'soc',
//     'temperature',
//     'dcPower',
//     'gridPower',
//     'pvProduction',
//     'dcBusVoltage',
//     'ACLoad',
//     'DCLoad'
//   ];
//
//   const chartData: chartDataInterface = {
//     soc: { name: 'State Of Charge', data: [] },
//     temperature: { name: 'Battery Temperature', data: [] },
//     dcPower: { name: 'Battery Power', data: [] },
//     gridPower: { name: 'Grid Power', data: [] },
//     pvProduction: { name: 'Pv Production', data: [] },
//     dcBusVoltage: { name: 'DC Bus Voltage', data: [] },
//     ACLoad: { name: 'AC Load', data: [] },
//     DCLoad: { name: 'DC Load', data: [] }
//   };
//
//   const chartOverview: overviewInterface = {
//     soc: { magnitude: 0, unit: '', min: 0, max: 0 },
//     temperature: { magnitude: 0, unit: '', min: 0, max: 0 },
//     dcPower: { magnitude: 0, unit: '', min: 0, max: 0 },
//     dcPowerWithoutHeating: { magnitude: 0, unit: '', min: 0, max: 0 },
//     gridPower: { magnitude: 0, unit: '', min: 0, max: 0 },
//     pvProduction: { magnitude: 0, unit: '', min: 0, max: 0 },
//     dcBusVoltage: { magnitude: 0, unit: '', min: 0, max: 0 },
//     overview: { magnitude: 0, unit: '', min: 0, max: 0 },
//     ACLoad: { magnitude: 0, unit: '', min: 0, max: 0 },
//     DCLoad: { magnitude: 0, unit: '', min: 0, max: 0 }
//   };
//
//   categories.forEach((category) => {
//     chartData[category].data = [];
//     chartOverview[category] = {
//       magnitude: 0,
//       unit: '',
//       min: MAX_NUMBER,
//       max: -MAX_NUMBER
//     };
//   });
//
//   let timestampArray: number[] = [];
//   let adjustedTimestampArray = [];
//   const timestampPromises = [];
//
//   await axiosConfig
//     .get(
//       `/GetCsvTimestampsForInstallation?id=${id}&start=${start_time.ticks}&end=${end_time.ticks}`
//     )
//     .then((res: AxiosResponse<number[]>) => {
//       timestampArray = res.data;
//     })
//     .catch((err: AxiosError) => {
//       if (err.response && err.response.status == 401) {
//         //removeToken();
//         //navigate(routes.login);
//       }
//     });
//
//   for (var i = 0; i < timestampArray.length; i++) {
//     timestampPromises.push(
//       fetchDataForOneTime(UnixTime.fromTicks(timestampArray[i]), s3Credentials)
//     );
//
//     const adjustedTimestamp = new Date(timestampArray[i] * 1000);
//     //Timezone offset is negative, so we convert the timestamp to the current zone by subtracting the corresponding offset
//     adjustedTimestamp.setHours(
//       adjustedTimestamp.getHours() - adjustedTimestamp.getTimezoneOffset() / 60
//     );
//     adjustedTimestampArray.push(adjustedTimestamp);
//   }
//
//   const results: Promise<FetchResult<Record<string, DataRecord>>>[] =
//     await Promise.all(timestampPromises);
//
//   for (let i = 0; i < results.length; i++) {
//     if (results[i] == null) {
//       // Handle not available or try later case
//     } else {
//       const timestamp = Object.keys(results[i])[
//         Object.keys(results[i]).length - 1
//       ];
//       const result = results[i][timestamp];
//       let category_index = 0;
//       // eslint-disable-next-line @typescript-eslint/no-loop-func
//       pathsToSearch.forEach((path) => {
//         if (result[path]) {
//           const value = result[path];
//
//           if (value.value < chartOverview[categories[category_index]].min) {
//             chartOverview[categories[category_index]].min = value.value;
//           }
//
//           if (value.value > chartOverview[categories[category_index]].max) {
//             chartOverview[categories[category_index]].max = value.value;
//           }
//           chartData[categories[category_index]].data.push([
//             adjustedTimestampArray[i],
//             value.value
//           ]);
//         } else {
//           //data[path].push([adjustedTimestamp, null]);
//         }
//         category_index++;
//       });
//     }
//   }
//
//   categories.forEach((category) => {
//     let value = Math.max(
//       Math.abs(chartOverview[category].max),
//       Math.abs(chartOverview[category].min)
//     );
//     let magnitude = 0;
//
//     if (value < 0) {
//       value = -value;
//     }
//     while (value >= 1000) {
//       value /= 1000;
//       magnitude++;
//     }
//     chartOverview[category].magnitude = magnitude;
//   });
//
//   chartOverview.soc.unit = '(%)';
//   chartOverview.soc.min = 0;
//   chartOverview.soc.max = 100;
//   chartOverview.temperature.unit = '(°C)';
//   chartOverview.dcPower.unit =
//     '(' + prefixes[chartOverview['dcPower'].magnitude] + 'W' + ')';
//   chartOverview.gridPower.unit =
//     '(' + prefixes[chartOverview['gridPower'].magnitude] + 'W' + ')';
//   chartOverview.pvProduction.unit =
//     '(' + prefixes[chartOverview['pvProduction'].magnitude] + 'W' + ')';
//   chartOverview.dcBusVoltage.unit =
//     '(' + prefixes[chartOverview['dcBusVoltage'].magnitude] + 'V' + ')';
//
//   chartOverview.overview = {
//     magnitude: Math.max(
//       chartOverview['gridPower'].magnitude,
//       chartOverview['pvProduction'].magnitude
//     ),
//     unit: '(kW)',
//     min: Math.min(
//       chartOverview['gridPower'].min,
//       chartOverview['pvProduction'].min
//     ),
//     max: Math.max(
//       chartOverview['gridPower'].max,
//       chartOverview['pvProduction'].max
//     )
//   };
//
//   return {
//     chartData: chartData,
//     chartOverview: chartOverview
//   };
// };
//
// export const transformInputToAggregatedData = async (
//   s3Credentials: I_S3Credentials,
//   start_date: dayjs.Dayjs,
//   end_date: dayjs.Dayjs
// ): Promise<{
//   chartAggregatedData: chartAggregatedDataInterface;
//   chartOverview: overviewInterface;
//   dateList: string[];
// }> => {
//   const data = {};
//   const overviewData = {};
//   const MAX_NUMBER = 9999999;
//   const dateList = [];
//
//   //let currentDate = dayjs();
//   let currentDay = start_date;
//   // type === 'weekly'
//   //   ? currentDate.subtract(1, 'week')
//   //   : currentDate.subtract(1, 'month');
//
//   const pathsToSearch = [
//     '/MinSoc',
//     '/MaxSoc',
//     '/PvPower',
//     '/DischargingBatteryPower',
//     '/ChargingBatteryPower',
//     '/GridImportPower',
//     '/GridExportPower',
//     '/HeatingPower'
//   ];
//
//   const categories = [
//     'minsoc',
//     'maxsoc',
//     'pvProduction',
//     'dcChargingPower',
//     'heatingPower',
//     'dcDischargingPower',
//     'gridImportPower',
//     'gridExportPower'
//   ];
//
//   const chartAggregatedData: chartAggregatedDataInterface = {
//     minsoc: { name: 'min SOC', data: [] },
//     maxsoc: { name: 'max SOC', data: [] },
//     pvProduction: { name: 'Pv Energy', data: [] },
//     dcChargingPower: { name: 'Charging Battery Energy', data: [] },
//     heatingPower: { name: 'Heating Energy', data: [] },
//     dcDischargingPower: { name: 'Discharging Battery Energy', data: [] },
//     gridImportPower: { name: 'Grid Import Energy', data: [] },
//     gridExportPower: { name: 'Grid Export Energy', data: [] }
//   };
//
//   const chartOverview: overviewInterface = {
//     soc: { magnitude: 0, unit: '', min: 0, max: 0 },
//     temperature: { magnitude: 0, unit: '', min: 0, max: 0 },
//     dcPower: { magnitude: 0, unit: '', min: 0, max: 0 },
//     dcPowerWithoutHeating: { magnitude: 0, unit: '', min: 0, max: 0 },
//     gridPower: { magnitude: 0, unit: '', min: 0, max: 0 },
//     pvProduction: { magnitude: 0, unit: '', min: 0, max: 0 },
//     dcBusVoltage: { magnitude: 0, unit: '', min: 0, max: 0 },
//     overview: { magnitude: 0, unit: '', min: 0, max: 0 },
//     ACLoad: { magnitude: 0, unit: '', min: 0, max: 0 },
//     DCLoad: { magnitude: 0, unit: '', min: 0, max: 0 }
//   };
//
//   pathsToSearch.forEach((path) => {
//     data[path] = [];
//     overviewData[path] = {
//       magnitude: 0,
//       unit: '',
//       min: MAX_NUMBER,
//       max: -MAX_NUMBER
//     };
//   });
//
//   const timestampPromises = [];
//
//   while (currentDay.isBefore(end_date)) {
//     timestampPromises.push(
//       fetchAggregatedData(currentDay.format('YYYY-MM-DD'), s3Credentials)
//     );
//     currentDay = currentDay.add(1, 'day');
//   }
//
//   const results = await Promise.all(timestampPromises);
//   currentDay = start_date;
//
//   for (let i = 0; i < results.length; i++) {
//     const result = results[i];
//     if (
//       result === FetchResult.notAvailable ||
//       result === FetchResult.tryLater
//     ) {
//       // Handle not available or try later case
//     } else {
//       dateList.push(currentDay.format('DD-MM'));
//       pathsToSearch.forEach((path) => {
//         if (result[path]) {
//           if (path === '/GridExportPower') {
//             result[path].value = -result[path].value;
//           }
//           if (result[path].value < overviewData[path].min) {
//             overviewData[path].min = result[path].value;
//           }
//           if (result[path].value > overviewData[path].max) {
//             overviewData[path].max = result[path].value;
//           }
//           // if (path === '/GridExportPower' && Math.abs(result[path].value) < 0.1) {
//           //   result[path].value = -0.3;
//           // }
//           data[path].push(result[path].value as number);
//         }
//       });
//     }
//     currentDay = currentDay.add(1, 'day');
//   }
//
//   pathsToSearch.forEach((path) => {
//     let value = Math.max(
//       Math.abs(overviewData[path].max),
//       Math.abs(overviewData[path].min)
//     );
//     let magnitude = 0;
//
//     if (value < 0) {
//       value = -value;
//     }
//     while (value >= 1000) {
//       value /= 1000;
//       magnitude++;
//     }
//     overviewData[path].magnitude = magnitude;
//   });
//
//   let path = '/MinSoc';
//   chartAggregatedData.minsoc.data = data[path];
//
//   path = '/MaxSoc';
//   chartAggregatedData.maxsoc.data = data[path];
//
//   chartOverview.soc = {
//     unit: '(%)',
//     magnitude: overviewData[path].magnitude,
//     min: 0,
//     max: 100
//   };
//
//   path = '/PvPower';
//   chartAggregatedData.pvProduction.data = data[path];
//
//   chartOverview.pvProduction = {
//     magnitude: overviewData[path].magnitude,
//     unit: '(kWh)',
//     min: overviewData[path].min,
//     max: overviewData[path].max
//   };
//
//   path = '/ChargingBatteryPower';
//   chartAggregatedData.dcChargingPower.data = data[path];
//
//   path = '/DischargingBatteryPower';
//   chartAggregatedData.dcDischargingPower.data = data[path];
//
//   path = '/HeatingPower';
//   chartAggregatedData.heatingPower.data = data[path];
//
//   chartOverview.dcPowerWithoutHeating = {
//     magnitude: Math.max(
//       overviewData['/ChargingBatteryPower'].magnitude,
//       overviewData['/DischargingBatteryPower'].magnitude
//     ),
//     unit: '(kWh)',
//     min: overviewData['/DischargingBatteryPower'].min,
//     max: overviewData['/ChargingBatteryPower'].max
//   };
//
//   chartOverview.dcPower = {
//     magnitude: Math.max(
//       overviewData['/ChargingBatteryPower'].magnitude,
//       overviewData['/HeatingPower'].magnitude,
//       overviewData['/DischargingBatteryPower'].magnitude
//     ),
//     unit: '(kWh)',
//     min: overviewData['/DischargingBatteryPower'].min,
//     max:
//       overviewData['/ChargingBatteryPower'].max +
//       overviewData['/HeatingPower'].max
//   };
//
//   path = '/GridImportPower';
//   chartAggregatedData.gridImportPower.data = data[path];
//
//   path = '/GridExportPower';
//   chartAggregatedData.gridExportPower.data = data[path];
//
//   chartOverview.gridPower = {
//     magnitude: Math.max(
//       overviewData['/GridImportPower'].magnitude,
//       overviewData['/GridExportPower'].magnitude
//     ),
//     unit: '(kWh)',
//     min: overviewData['/GridExportPower'].min,
//     max: overviewData['/GridImportPower'].max
//   };
//
//   chartOverview.overview = {
//     magnitude: 0,
//     unit: '(kWh)',
//     min: Math.min(
//       overviewData['/GridImportPower'].min,
//       overviewData['/GridExportPower'].min,
//       overviewData['/PvPower'].min
//     ),
//     max: Math.max(
//       overviewData['/GridImportPower'].max,
//       overviewData['/GridExportPower'].max,
//       overviewData['/PvPower'].max
//     )
//   };
//
//   return {
//     chartAggregatedData: chartAggregatedData,
//     chartOverview: chartOverview,
//     dateList: dateList
//   };
// };
